import { useRef, useState, useEffect } from 'react';
import * as THREE from 'three';

// Soft circular sprite used for smoke particles
function makeSmokeTexture() {
  const size = 128;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.25, 'rgba(255,255,255,0.55)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
  return new THREE.CanvasTexture(canvas);
}

// Lightweight CSS fallback for mobile / no-WebGL
function SmokeFallback() {
  return (
    <div className="absolute inset-0 overflow-hidden bg-[#0A0A0A]" data-testid="hero-3d-fallback">
      <div className="smoke-fallback-a absolute -top-1/4 -left-1/4 w-[80vw] h-[80vw] rounded-full bg-red-700/25 blur-[90px]" />
      <div className="smoke-fallback-b absolute top-1/3 right-0 w-[70vw] h-[70vw] rounded-full bg-red-600/20 blur-[100px]" />
      <div className="absolute bottom-0 left-1/4 w-[60vw] h-[40vw] rounded-full bg-red-900/30 blur-[80px]" />
    </div>
  );
}

export default function Hero3D() {
  const containerRef = useRef(null);
  const [useCanvas, setUseCanvas] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= 768
  );

  useEffect(() => {
    const onResize = () => setUseCanvas(window.innerWidth >= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!useCanvas) return undefined;
    const container = containerRef.current;
    if (!container) return undefined;

    let width = container.clientWidth || window.innerWidth;
    let height = container.clientHeight || window.innerHeight;

    // --- renderer / scene / camera ---
    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    } catch (e) {
      return undefined; // WebGL unavailable -> CSS fallback stays
    }
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setSize(width, height);
    renderer.setClearColor('#0A0A0A', 1);
    container.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 100);
    camera.position.set(0, 0, 9);

    // --- lights ---
    scene.add(new THREE.AmbientLight(0xffffff, 0.4));
    const key = new THREE.PointLight(0xef4444, 120, 40);
    key.position.set(6, 4, 6);
    scene.add(key);
    const fill = new THREE.PointLight(0x7f1d1d, 60, 40);
    fill.position.set(-8, -3, 2);
    scene.add(fill);

    // --- smoke particles ---
    const COUNT = 650;
    const positions = new Float32Array(COUNT * 3);
    const colors = new Float32Array(COUNT * 3);
    const speeds = new Float32Array(COUNT);
    const red = new THREE.Color('#DC2626');
    const ember = new THREE.Color('#7F1D1D');
    const ash = new THREE.Color('#1a1a1a');
    for (let i = 0; i < COUNT; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 18;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 13;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 11 - 2;
      const r = Math.random();
      const c = r < 0.4 ? red.clone() : r < 0.7 ? ember.clone() : ash.clone();
      c.lerp(red, Math.random() * 0.4);
      colors[i * 3] = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;
      speeds[i] = 0.15 + Math.random() * 0.5;
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    const texture = makeSmokeTexture();
    const material = new THREE.PointsMaterial({
      size: 1.7,
      map: texture,
      vertexColors: true,
      transparent: true,
      opacity: 0.55,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true
    });
    const points = new THREE.Points(geometry, material);
    scene.add(points);

    // --- glowing medical cross ---
    const crossGroup = new THREE.Group();
    const crossMat = new THREE.MeshStandardMaterial({
      color: '#DC2626',
      emissive: '#DC2626',
      emissiveIntensity: 1.4,
      metalness: 0.4,
      roughness: 0.25
    });
    const vert = new THREE.Mesh(new THREE.BoxGeometry(0.85, 2.6, 0.6), crossMat);
    const horiz = new THREE.Mesh(new THREE.BoxGeometry(2.6, 0.85, 0.6), crossMat);
    crossGroup.add(vert, horiz);
    crossGroup.position.set(4.2, 0.4, -1.5);
    crossGroup.scale.setScalar(0.9);
    scene.add(crossGroup);

    // --- animation loop ---
    const clock = new THREE.Clock();
    let raf;
    const posAttr = geometry.attributes.position;
    const animate = () => {
      const delta = clock.getDelta();
      const t = clock.elapsedTime;
      const arr = posAttr.array;
      for (let i = 0; i < COUNT; i++) {
        arr[i * 3 + 1] += speeds[i] * delta;
        arr[i * 3] += Math.sin(t * 0.3 + i) * 0.002;
        if (arr[i * 3 + 1] > 6.5) arr[i * 3 + 1] = -6.5;
      }
      posAttr.needsUpdate = true;
      points.rotation.z += delta * 0.015;
      crossGroup.position.y = 0.4 + Math.sin(t * 0.6) * 0.45;
      crossGroup.rotation.y = Math.sin(t * 0.25) * 0.5;
      crossGroup.rotation.z = Math.sin(t * 0.15) * 0.08;
      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    // --- resize ---
    const handleResize = () => {
      if (!container) return;
      width = container.clientWidth || window.innerWidth;
      height = container.clientHeight || window.innerHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener('resize', handleResize);

    // --- cleanup ---
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', handleResize);
      geometry.dispose();
      material.dispose();
      texture.dispose();
      vert.geometry.dispose();
      horiz.geometry.dispose();
      crossMat.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [useCanvas]);

  if (!useCanvas) {
    return <SmokeFallback />;
  }

  return <div ref={containerRef} className="absolute inset-0" data-testid="hero-3d-canvas" />;
}
